import { useEffect, useState } from "react";
import { Routes, Route, Outlet } from "react-router";
import { Home } from "../features/home/Home";
import { AuthProvider } from "../features/authentication/context/AuthProvider";
import OAuthCallback from "../features/authentication/components/OAuthCallback";
import MainLayout from "../components/Layout/MainLayout";
import NotFound from "../components/NotFound/NotFound";
import { useAuth } from "../features/authentication/hooks/useAuth";
import { getItem, removeItem } from "../features/authentication/utils/handleLocalStorage";
import { Projects } from "../features/projects/Projects";
import { DetailProject } from "../features/projects/components/projectPage/components/DetailProject/DetailProject";
import { ValidateSession } from "../features/authentication/components/ValidateSession";
import Assignments from "@/features/assignments/Assignments";
import { AdminTabs } from "@/features/users/components/AdminTabs";

// Dummies para rutas nuevas
const Dummy = ({ name }: { name: string }) => <div style={{ padding: 32, fontSize: 24 }}>Página: {name}</div>;

const LOGIN_URL = import.meta.env.VITE_LOGIN_URL;

export default function SessionValidator() {
  const { isAuthenticated, token, authenticate } = useAuth();
  const [isValidating, setIsValidating] = useState(true);
  const [validationMessage, setValidationMessage] = useState("Iniciando aplicación...");

  useEffect(() => {
    console.log('🔄 SessionValidator - Starting validation:', { isAuthenticated, hasToken: !!token });
    
    const validateSession = async () => {
      try {
        setValidationMessage("Verificando autenticación...");
        
        // Si ya está autenticado, no hacer nada más
        if (isAuthenticated && token) {
          console.log('✅ Already authenticated');
          setIsValidating(false);
          return;
        }

        // Verificar si hay un token válido
        if (token && !isAuthenticated) {
          console.log('🔍 Token found, waiting for validation...');
          setValidationMessage("Validando credenciales...");
          // Dar tiempo para que el AuthProvider valide el token
          setTimeout(() => {
            setIsValidating(false);
          }, 2000);
          return;
        }

        // Si no hay token ni autenticación
        if (!isAuthenticated && !token) {
          const isLoggingOut = getItem("isLoggingOut");
          
          if (isLoggingOut) {
            console.log('🚪 User is logging out, redirecting...');
            removeItem("isLoggingOut");
            window.location.href = LOGIN_URL;
            return;
          }

          console.log('🔐 No authentication found, starting OAuth flow...');
          setValidationMessage("Redirigiendo a autenticación...");
          
          // Dar un momento antes de redirigir
          setTimeout(() => {
            authenticate();
          }, 1000);
        }

      } catch (error) {
        console.error("❌ Error durante la autenticación:", error);
        setValidationMessage("Error de autenticación");
        removeItem("token");
        removeItem("oauthCodeProcessed");
        
        setTimeout(() => {
          window.location.href = LOGIN_URL;
        }, 2000);
      } finally {
        // Timeout de seguridad para evitar que se quede colgado
        setTimeout(() => {
          setIsValidating(false);
        }, 8000);
      }
    };

    validateSession();
  }, [isAuthenticated, token, authenticate]);

  // Mostrar loader mientras valida
  if (isValidating) {
    return <ValidateSession message={validationMessage} />;
  }

  // Si no está autenticado después de la validación
  if (!isAuthenticated) {
    return <ValidateSession message="Autenticación requerida" />;
  }

  // Usuario autenticado, mostrar la aplicación
  console.log('✅ User authenticated, rendering app');
  return <Outlet />;
}

export const AppRouter = () => {
  return (
    <div data-testid="app-router">
      <AuthProvider>
        <Routes>
          <Route path="callback" element={<OAuthCallback />} />
          <Route element={<SessionValidator />}>
            <Route element={<MainLayout />}>
              <Route index element={< Home />} />
              <Route path="admin" element={<AdminTabs />} />
              <Route path="projects" >
                <Route index element={<Projects />} />
                <Route path=":projectId" element={<DetailProject />} />
                <Route path=":projectId/task/:taskId" element={<DetailProject />} />
              </Route>
              <Route path="assignments" element={<Assignments/>} />
              {/* Rutas dummy para navegación del sidebar */}
              <Route path="inbox" element={<Dummy name="Inbox" />} />
              <Route path="reporting" element={<Dummy name="Reporting" />} />
              <Route path="portfolios" element={<Dummy name="Portfolios" />} />
              <Route path="goals" element={<Dummy name="Goals" />} />
              <Route path="invite" element={<Dummy name="Invite" />} />
              <Route path="help" element={<Dummy name="Help" />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider >
    </div >
  )
};
